import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useState } from 'react';
import {
  FaChevronDown,
  FaFilter,
  FaMagnifyingGlass,
  FaXmark,
} from 'react-icons/fa6';
import {
  detectSearchType,
  NfseReportFilters as FiltersType,
  useNfseReportContext,
} from '../NfseReportContext';
import { NFSE_REPORT_STATUS_LABELS } from '../types/nfse.types';

const getCurrentReferenceMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}/${month}`;
};

const getSearchTypeLabel = (value: string): string => {
  if (!value.trim()) return '';

  const searchParams = detectSearchType(value);

  if (searchParams.taxCode) return 'Cód. Tributário';
  if (searchParams.nbsCode) return 'Cód. NBS';
  if (searchParams.document) return 'Documento';
  if (searchParams.accessKey) return 'Chave de Acesso';

  return 'Pesquisa';
};

const statusStringToArray = (status: string): string[] => {
  if (!status) return [];
  return status.split(',').filter(Boolean);
};

const statusArrayToString = (statuses: string[]): string => {
  return statuses.join(',');
};

type DateFilterType = 'competencia' | 'dataCriacao';

const getInitialDateFilterType = (filters: FiltersType): DateFilterType => {
  if (filters.referenceMonth) return 'competencia';
  return 'dataCriacao';
};

const formatReferenceMonthToApi = (value: string): string => {
  if (!value) return '';
  return value.replace('-', '/');
};

const formatReferenceMonthToInput = (value: string): string => {
  if (!value) return '';
  return value.replace('/', '-');
};

const NfseReportFilters = () => {
  const { filters, setFilters } = useNfseReportContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [localFilters, setLocalFilters] = useState<FiltersType>(filters);
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    statusStringToArray(filters.status),
  );
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>(
    getInitialDateFilterType(filters),
  );

  const handleModalFilterChange = (field: keyof FiltersType, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateFilterTypeChange = (type: DateFilterType) => {
    setDateFilterType(type);
    if (type === 'competencia') {
      setLocalFilters((prev) => ({
        ...prev,
        startDateTime: '',
        endDateTime: '',
      }));
    } else {
      setLocalFilters((prev) => ({
        ...prev,
        referenceMonth: '',
      }));
    }
  };

  const handleStatusChange = (status: string) => {
    const allStatuses = Object.keys(NFSE_REPORT_STATUS_LABELS);

    if (status === 'ALL') {
      if (selectedStatuses.length === allStatuses.length) {
        setSelectedStatuses([]);
      } else {
        setSelectedStatuses([...allStatuses]);
      }
    } else {
      setSelectedStatuses((prev) =>
        prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev, status],
      );
    }
  };

  const handleApplyModalFilters = () => {
    let updatedFilters: FiltersType;

    if (dateFilterType === 'competencia') {
      updatedFilters = {
        ...localFilters,
        startDateTime: '',
        endDateTime: '',
        referenceMonth: formatReferenceMonthToApi(localFilters.referenceMonth),
        status: statusArrayToString(selectedStatuses),
        searchTerm,
      };
    } else {
      updatedFilters = {
        ...localFilters,
        referenceMonth: '',
        status: statusArrayToString(selectedStatuses),
        searchTerm,
      };
    }

    setFilters(updatedFilters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters: FiltersType = {
      startDateTime: '',
      endDateTime: '',
      status: '',
      referenceMonth: getCurrentReferenceMonth(),
      searchTerm: '',
    };
    setLocalFilters(resetFilters);
    setSelectedStatuses([]);
    setSearchTerm('');
    setDateFilterType('competencia');
    setFilters(resetFilters);
    onClose();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setFilters({ ...filters, searchTerm });
    }
  };

  const handleSearchSubmit = () => {
    setFilters({ ...filters, searchTerm });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
  };

  const searchTypeLabel = getSearchTypeLabel(searchTerm);

  const hasActiveFilters =
    filters.status !== '' || filters.referenceMonth !== '';

  const formatDateDisplay = (date: string): string => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatReferenceMonthDisplay = (month: string): string => {
    if (!month) return '';
    const [year, m] = month.split('/');
    return `${m}/${year}`;
  };

  const getActiveFilterTags = (): Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }> => {
    const tags: Array<{ key: string; label: string; onRemove: () => void }> =
      [];

    if (filters.startDateTime && filters.endDateTime) {
      tags.push({
        key: 'dateRange',
        label: `Data: ${formatDateDisplay(filters.startDateTime)} até ${formatDateDisplay(filters.endDateTime)}`,
        onRemove: () => {
          const defaultReferenceMonth = getCurrentReferenceMonth();
          setFilters({
            ...filters,
            startDateTime: '',
            endDateTime: '',
            referenceMonth: defaultReferenceMonth,
          });
          setLocalFilters((prev) => ({
            ...prev,
            startDateTime: '',
            endDateTime: '',
            referenceMonth: defaultReferenceMonth,
          }));
          setDateFilterType('competencia');
        },
      });
    }

    if (filters.referenceMonth) {
      tags.push({
        key: 'referenceMonth',
        label: `Competência: ${formatReferenceMonthDisplay(filters.referenceMonth)}`,
        onRemove: () => {
          const defaultReferenceMonth = getCurrentReferenceMonth();
          setFilters({
            ...filters,
            referenceMonth: defaultReferenceMonth,
          });
          setLocalFilters((prev) => ({
            ...prev,
            referenceMonth: defaultReferenceMonth,
          }));
        },
      });
    }

    const activeStatuses = statusStringToArray(filters.status);
    activeStatuses.forEach((status) => {
      const statusLabel =
        NFSE_REPORT_STATUS_LABELS[
          status as keyof typeof NFSE_REPORT_STATUS_LABELS
        ] || status;
      tags.push({
        key: `status-${status}`,
        label: `Status: ${statusLabel}`,
        onRemove: () => {
          const newStatuses = activeStatuses.filter((s) => s !== status);
          const newStatusString = statusArrayToString(newStatuses);
          setFilters({ ...filters, status: newStatusString });
          setSelectedStatuses(newStatuses);
        },
      });
    });

    return tags;
  };

  const activeFilterTags = getActiveFilterTags();

  return (
    <>
      <HStack spacing={4} mb={4}>
        <FormControl flex={1}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <FaMagnifyingGlass color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Pesquisar por Cód. Tributário (6), NBS (9), CPF/CNPJ (11/14) ou Chave de Acesso (50+)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyPress}
              pr={searchTerm ? '4.5rem' : '3rem'}
            />
            {searchTerm && (
              <InputRightElement width="4.5rem">
                <HStack spacing={1}>
                  <Tooltip label="Limpar pesquisa" hasArrow>
                    <IconButton
                      aria-label="Limpar pesquisa"
                      icon={<FaXmark />}
                      size="xs"
                      variant="ghost"
                      onClick={handleClearSearch}
                    />
                  </Tooltip>
                </HStack>
              </InputRightElement>
            )}
          </InputGroup>
          {searchTypeLabel && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              Buscando por: {searchTypeLabel}
            </Text>
          )}
        </FormControl>

        <Button
          leftIcon={<FaMagnifyingGlass />}
          colorScheme="yooga"
          onClick={handleSearchSubmit}
        >
          Buscar
        </Button>

        <Tooltip label="Filtros avançados" hasArrow>
          <IconButton
            aria-label="Filtros avançados"
            icon={<FaFilter />}
            colorScheme={hasActiveFilters ? 'yooga' : 'gray'}
            variant={hasActiveFilters ? 'solid' : 'outline'}
            onClick={onOpen}
          />
        </Tooltip>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleResetFilters}>
            Limpar Filtros
          </Button>
        )}
      </HStack>

      {activeFilterTags.length > 0 && (
        <Wrap spacing={2} mb={4}>
          {activeFilterTags.map((tag) => (
            <WrapItem key={tag.key}>
              <Tag
                size="md"
                borderRadius="full"
                variant="solid"
                colorScheme="yooga"
              >
                <TagLabel>{tag.label}</TagLabel>
                <TagCloseButton onClick={tag.onRemove} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filtros Avançados</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <FormLabel mb={2}>Filtrar por</FormLabel>
                <ButtonGroup size="sm" isAttached variant="outline" w="100%">
                  <Button
                    flex={1}
                    colorScheme={
                      dateFilterType === 'competencia' ? 'yooga' : 'gray'
                    }
                    variant={
                      dateFilterType === 'competencia' ? 'solid' : 'outline'
                    }
                    onClick={() => handleDateFilterTypeChange('competencia')}
                  >
                    Competência
                  </Button>
                  <Button
                    flex={1}
                    colorScheme={
                      dateFilterType === 'dataCriacao' ? 'yooga' : 'gray'
                    }
                    variant={
                      dateFilterType === 'dataCriacao' ? 'solid' : 'outline'
                    }
                    onClick={() => handleDateFilterTypeChange('dataCriacao')}
                  >
                    Data de Criação
                  </Button>
                </ButtonGroup>
              </Box>

              {dateFilterType === 'competencia' ? (
                <FormControl>
                  <FormLabel>Mês de Competência</FormLabel>
                  <Input
                    type="month"
                    value={formatReferenceMonthToInput(
                      localFilters.referenceMonth,
                    )}
                    onChange={(e) =>
                      handleModalFilterChange('referenceMonth', e.target.value)
                    }
                  />
                </FormControl>
              ) : (
                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel>Data inicial</FormLabel>
                    <Input
                      type="date"
                      value={localFilters.startDateTime}
                      onChange={(e) =>
                        handleModalFilterChange('startDateTime', e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Data final</FormLabel>
                    <Input
                      type="date"
                      value={localFilters.endDateTime}
                      onChange={(e) =>
                        handleModalFilterChange('endDateTime', e.target.value)
                      }
                    />
                  </FormControl>
                </HStack>
              )}

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      rightIcon={<FaChevronDown />}
                      w="100%"
                      textAlign="left"
                      fontWeight="normal"
                    >
                      {selectedStatuses.length === 0
                        ? 'Todos'
                        : selectedStatuses.length ===
                            Object.keys(NFSE_REPORT_STATUS_LABELS).length
                          ? 'Todos'
                          : `${selectedStatuses.length} selecionado${selectedStatuses.length > 1 ? 's' : ''}`}
                    </MenuButton>
                    <MenuList minWidth="200px">
                      <MenuItem onClick={() => handleStatusChange('ALL')}>
                        <Checkbox
                          isChecked={
                            selectedStatuses.length ===
                            Object.keys(NFSE_REPORT_STATUS_LABELS).length
                          }
                          pointerEvents="none"
                        >
                          Todos
                        </Checkbox>
                      </MenuItem>
                      {Object.entries(NFSE_REPORT_STATUS_LABELS).map(
                        ([key, label]) => (
                          <MenuItem
                            key={key}
                            onClick={() => handleStatusChange(key)}
                          >
                            <Checkbox
                              isChecked={selectedStatuses.includes(key)}
                              pointerEvents="none"
                            >
                              {label}
                            </Checkbox>
                          </MenuItem>
                        ),
                      )}
                    </MenuList>
                  </Menu>
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleResetFilters}>
              Limpar Filtros
            </Button>
            <Button colorScheme="yooga" onClick={handleApplyModalFilters}>
              Aplicar Filtros
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default NfseReportFilters;
